<template>
  <div
    v-bind='$attrs'
    ref='field'
  >
    <label
      v-if='label'
      :for='name'
      class='block w-full max-w-[49%] gap-0.5 truncate text-sm font-normal'
      :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700"]'
    >
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <template v-if='editMode'>
      <div class='relative mt-1 w-full'>
        <div
          v-if='withIcon'
          class='pointer-events-none absolute inset-y-0 left-3 flex items-center py-3'
        >
          <component
            :is='icon'
            class='h-6 w-6 shrink-0'
            aria-hidden='true'
          />
        </div>
        <div
          v-if='color'
          class='flex flex-row items-center'
        >
          <input
            :id='name'
            data-cy='string-field-input'
            :value='getFieldValue(modelValue)'
            :name='name'
            :type='type'
            :placeholder='placeholder'
            :aria-describedby='ariaDescribedBy'
            :disabled='disabled'
            class='block w-full rounded-md text-sm shadow-sm'
            :class='[
              error ? "border-red-300 focus:ring-red-500 focus:border-red-500 caret-red-500 selection:bg-red-300 selection:text-red-900" : "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
              disabled ? "bg-gray-50 text-gray-400" : "",
              withIcon ? "pl-11" : "",
              inputClasses
            ]'
            @input='updateValue($event.target.value)'
            @focus='$emit("has-focus", name)'
          >
          <button
            class='h-100 absolute right-0 w-14 rounded-r-md py-1.5 shadow-sm focus:border focus:border-gray-500 focus:ring-gray-500'
            :style='{ background: color }'
            @click='showColorPicker = !showColorPicker'
          >
            <AdjustmentsIcon class='m-auto h-6 w-6 shrink-0 text-gray-700'/>
          </button>
        </div>
        <div
          v-show='showColorPicker'
          class='absolute z-50'
        >
          <ColorPicker
            theme='light'
            :color='color'
            :sucker-hide='false'
            @change-color='changeColor'
          />
        </div>
      </div>
      <InputError :error='error'/>
    </template>
    <template v-else>
      <div class='flex flex-row items-center truncate'>
        <span
          data-cy='preview-value'
          :title='modelValue'
          :class='[previewClasses ? previewClasses : "text-sm"]'
        >
          {{ getFieldValue(modelValue) }}
        </span>
        <button
          class='ml-4 w-12 rounded-full text-xs shadow-sm'
          :style='{ background: color }'
        >
          &nbsp;
        </button>
      </div>

    </template>
  </div>
  <Modal
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>
</template>

<script lang="ts">
  import { defineComponent, watch, onMounted, ref } from 'vue'
  import useInputFieldBase, { InputFieldProps, InputFieldComponents, InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import { ColorPicker } from 'vue-color-kit'
  import 'vue-color-kit/dist/vue-color-kit.css'
  import { AdjustmentsIcon } from '@heroicons/vue/outline'
  import { onClickOutside } from '@vueuse/core'
  import { dom } from '@/utils/dom'

  export default defineComponent({
    name: 'ColorField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      ColorPicker,
      AdjustmentsIcon,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      type: {
        type: String,
        default: 'text',
      },
      withIcon: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
        default: null,
      },
    },
    emits: [
      ...InputFieldEmits,
      'has-focus',
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, getLabel } = useInputFieldBase()
      const color = ref(props.modelValue)
      const showColorPicker = ref(false)
      const field = ref(dom())

      onMounted(() => {
        color.value = color.value ?? '#9ca3af'
      })

      watch(color, (newVal, oldVal) => {
        if (newVal !== oldVal) {
          updateValue(color.value)
        }
      })

      const getFieldValue = (value: object): string => {
        return color.value ?? value
      }

      onClickOutside(field, () => togglePicker(false))

      const togglePicker = (value: boolean) => {
        showColorPicker.value = value
      }

      const changeColor = (colorPicker: object) => {
        color.value = colorPicker.hex
        updateValue(color.value)
      }

      return {
        updateValue,
        getLabel,
        field,
        helpVisible,
        setHelpModalVisibility,
        getFieldValue,
        changeColor,
        showColorPicker,
        color,
      }
    },
  })
</script>
